import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./EditProfile.module.css";
import {
  useNavigate,
  useParams,
} from "react-router-dom";

export default function EditProfile({
  onBack,
  onUpdate,
}) {
  // =====================================================
  // URL PARAMETERS
  // =====================================================

  const {
    userId: routeUserId,
    deviceUuid: routeDeviceUuid,
  } = useParams();

  const navigate = useNavigate();

  // =====================================================
  // API
  // =====================================================

  const API_BASE =
    "https://coinyoco.world/brobizee-api";

  const GET_USER_API = `${API_BASE}/get_user.php`;
  const UPDATE_USER_API = `${API_BASE}/update_username.php`;
  const DELETE_USER_API = `${API_BASE}/delete_user.php`;

  /*
  =====================================================
  OPTIONAL: HttpOnly cookies clear karne ke liye
  backend endpoint (agar available hai).
  Nahi hai to isko null chhod do - JS wali
  cookies fir bhi clear ho jayengi.
  =====================================================
  */

  const CLEAR_SITE_DATA_API =
    `${API_BASE}/clear_site_data.php`;

  // =====================================================
  // USER ID / DEVICE UUID
  // =====================================================

  const userId =
    routeUserId ||
    localStorage.getItem("user_id");

  const deviceUuid =
    routeDeviceUuid ||
    localStorage.getItem("device_uuid");

  // =====================================================
  // PROFILE STATE
  // =====================================================

  const [profileImg, setProfileImg] =
    useState(null);

  const [profileFile, setProfileFile] =
    useState(null);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phoneNo, setPhoneNo] = useState("");

  const [darkMode, setDarkMode] = useState(false);

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");

  // =====================================================
  // USER NOT FOUND STATE (NEW)
  // =====================================================

  const [userNotFound, setUserNotFound] =
    useState(false);

  // =====================================================
  // LOCATION
  // =====================================================

  const [locationLoading, setLocationLoading] =
    useState(false);

  const [locationError, setLocationError] =
    useState("");

  // =====================================================
  // DELETE
  // =====================================================

  const [showDeletePopup, setShowDeletePopup] =
    useState(false);

  const [deleting, setDeleting] = useState(false);

  // =====================================================
  // CLEAR SITE DATA
  // =====================================================

  const [clearing, setClearing] = useState(false);
  const [clearStatus, setClearStatus] = useState("");

  // =====================================================
  // ERROR POPUP
  // =====================================================

  const [showNetworkError, setShowNetworkError] =
    useState(false);

  const [networkErrorMessage, setNetworkErrorMessage] =
    useState("");

  // =====================================================
  // FILE REF
  // =====================================================

  const fileInputRef = useRef(null);

  // =====================================================
  // SHOW ERROR POPUP
  // =====================================================

  const showErrorPopup = (message) => {
    setNetworkErrorMessage(
      message ||
        "Something went wrong. Please try again."
    );

    setShowNetworkError(true);
  };

  // =====================================================
  // IMAGE URL
  // =====================================================

  const makeImageUrl = (imagePath) => {
    if (!imagePath) {
      return "";
    }

    const path = String(imagePath).trim();

    if (!path) {
      return "";
    }

    if (
      path.startsWith("http://") ||
      path.startsWith("https://") ||
      path.startsWith("blob:")
    ) {
      return path;
    }

    return `${API_BASE}/${path.replace(/^\/+/, "")}`;
  };

  // =====================================================
  // FETCH USER
  // =====================================================

  useEffect(() => {
    let cancelled = false;

    const fetchUser = async () => {
      try {
        setLoading(true);
        setError("");
        setUserNotFound(false);

        if (!userId) {
          throw new Error("User ID not found.");
        }

        const params = new URLSearchParams();

        params.append("user_id", String(userId));

        if (deviceUuid) {
          params.append(
            "device_uuid",
            String(deviceUuid)
          );
        }

        const response = await fetch(
          `${GET_USER_API}?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        const responseText = await response.text();

        console.log(
          "GET USER RESPONSE:",
          responseText
        );

        if (!response.ok) {
          throw new Error(
            `HTTP Error: ${response.status}`
          );
        }

        let data;

        try {
          data = JSON.parse(responseText);
        } catch (jsonError) {
          console.error(
            "GET USER INVALID JSON:",
            responseText
          );

          throw new Error(
            "Invalid JSON returned by get_user.php"
          );
        }

        console.log("GET USER DATA:", data);

        if (!data.success) {
          setUserNotFound(true);

          throw new Error(
            data.message || "Unable to load user."
          );
        }

        if (cancelled) {
          return;
        }

        const user = data.user || {};

        setName(user.name || "");

        const savedLocation =
          user.location_live || "";

        setAddress(savedLocation);

        setPhoneNo(user.mobile_number || "");

        const imageUrl =
          user.profile_url ||
          makeImageUrl(user.profile_user);

        setProfileImg(imageUrl || null);

        if (user.id) {
          localStorage.setItem(
            "user_id",
            String(user.id)
          );
        }

        if (user.device_uuid) {
          localStorage.setItem(
            "device_uuid",
            user.device_uuid
          );
        }

        if (user.name) {
          localStorage.setItem(
            "user_name",
            user.name
          );
        }

        if (savedLocation) {
          localStorage.setItem(
            "user_address",
            savedLocation
          );
        }

        if (user.profile_user) {
          localStorage.setItem(
            "profile_user",
            user.profile_user
          );
        }

        if (imageUrl) {
          localStorage.setItem(
            "profile_image_url",
            imageUrl
          );
        }
      } catch (err) {
        console.error("FETCH USER ERROR:", err);

        if (!cancelled) {
          const message =
            err?.message ||
            "Unable to load profile.";

          setError(message);

          showErrorPopup(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchUser();

    return () => {
      cancelled = true;
    };
  }, [userId, deviceUuid]);

  // =====================================================
  // GET CURRENT LOCATION
  // =====================================================

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(
          new Error(
            "Geolocation is not supported by this browser."
          )
        );

        return;
      }

      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );
    });
  };

  // =====================================================
  // REVERSE GEOCODING
  // =====================================================

  const getAddressFromCoordinates = async (
    latitude,
    longitude
  ) => {
    const url =
      "https://nominatim.openstreetmap.org/reverse" +
      `?format=jsonv2` +
      `&lat=${encodeURIComponent(latitude)}` +
      `&lon=${encodeURIComponent(longitude)}` +
      `&zoom=18` +
      `&addressdetails=1`;

    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        "Unable to get address from location."
      );
    }

    const data = await response.json();

    if (!data.display_name) {
      throw new Error("Address not found.");
    }

    return data.display_name;
  };

  // =====================================================
  // CURRENT ADDRESS
  // =====================================================

  const loadCurrentAddress = async () => {
    try {
      setLocationLoading(true);
      setLocationError("");

      const position = await getCurrentLocation();

      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;

      console.log("CURRENT LATITUDE:", latitude);
      console.log("CURRENT LONGITUDE:", longitude);

      const currentAddress =
        await getAddressFromCoordinates(
          latitude,
          longitude
        );

      console.log(
        "CURRENT ADDRESS:",
        currentAddress
      );

      setAddress(currentAddress);

      localStorage.setItem(
        "current_latitude",
        String(latitude)
      );

      localStorage.setItem(
        "current_longitude",
        String(longitude)
      );

      localStorage.setItem(
        "current_address",
        currentAddress
      );

      localStorage.setItem(
        "user_address",
        currentAddress
      );
    } catch (err) {
      console.error("LOCATION ERROR:", err);

      let message =
        "Unable to get current location.";

      if (err.code === 1) {
        message =
          "Location permission was denied. Please allow location permission.";
      } else if (err.code === 2) {
        message =
          "Current location is unavailable.";
      } else if (err.code === 3) {
        message = "Location request timed out.";
      } else if (err.message) {
        message = err.message;
      }

      setLocationError(message);

      showErrorPopup(message);
    } finally {
      setLocationLoading(false);
    }
  };

  // =====================================================
  // AUTO LOCATION
  // =====================================================

  useEffect(() => {
    if (loading) {
      return;
    }

    if (address.trim()) {
      return;
    }

    loadCurrentAddress();
  }, [loading]);

  // =====================================================
  // IMAGE COMPRESSION - MAX 250KB
  // =====================================================

  const compressImageTo250KB = (
    file,
    maxSizeKB = 250
  ) => {
    return new Promise((resolve, reject) => {
      if (!file || !file.type.startsWith("image/")) {
        reject(new Error("Invalid image file."));
        return;
      }

      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();

        img.onload = () => {
          try {
            const MAX_SIZE = maxSizeKB * 1024;

            let width = img.width;
            let height = img.height;

            const MAX_DIMENSION = 2400;

            if (
              width > MAX_DIMENSION ||
              height > MAX_DIMENSION
            ) {
              const ratio = Math.min(
                MAX_DIMENSION / width,
                MAX_DIMENSION / height
              );

              width = Math.round(width * ratio);
              height = Math.round(height * ratio);
            }

            const canvas =
              document.createElement("canvas");

            const ctx =
              canvas.getContext("2d");

            if (!ctx) {
              reject(
                new Error(
                  "Unable to process image."
                )
              );
              return;
            }

            const compressWithQuality = (
              targetWidth,
              targetHeight,
              quality
            ) => {
              return new Promise((resolveBlob) => {
                canvas.width = targetWidth;
                canvas.height = targetHeight;

                ctx.clearRect(
                  0,
                  0,
                  targetWidth,
                  targetHeight
                );

                ctx.fillStyle = "#ffffff";

                ctx.fillRect(
                  0,
                  0,
                  targetWidth,
                  targetHeight
                );

                ctx.drawImage(
                  img,
                  0,
                  0,
                  targetWidth,
                  targetHeight
                );

                canvas.toBlob(
                  (blob) => {
                    resolveBlob(blob);
                  },
                  "image/jpeg",
                  quality
                );
              });
            };

            const runCompression = async () => {
              let targetWidth = width;
              let targetHeight = height;

              for (
                let dimensionTry = 0;
                dimensionTry < 15;
                dimensionTry++
              ) {
                for (
                  let qualityTry = 0;
                  qualityTry < 11;
                  qualityTry++
                ) {
                  const quality =
                    0.92 - qualityTry * 0.08;

                  const blob =
                    await compressWithQuality(
                      targetWidth,
                      targetHeight,
                      Math.max(0.1, quality)
                    );

                  if (
                    blob &&
                    blob.size <= MAX_SIZE
                  ) {
                    resolve(
                      new File(
                        [blob],
                        "profile-image.jpg",
                        {
                          type: "image/jpeg",
                          lastModified:
                            Date.now(),
                        }
                      )
                    );

                    return;
                  }
                }

                targetWidth = Math.max(
                  320,
                  Math.round(targetWidth * 0.8)
                );

                targetHeight = Math.max(
                  320,
                  Math.round(targetHeight * 0.8)
                );
              }

              reject(
                new Error(
                  "Image could not be compressed below 250KB."
                )
              );
            };

            runCompression().catch(reject);
          } catch (error) {
            reject(error);
          }
        };

        img.onerror = () => {
          reject(
            new Error(
              "Unable to read selected image."
            )
          );
        };

        img.src = event.target.result;
      };

      reader.onerror = () => {
        reject(
          new Error("Unable to read image file.")
        );
      };

      reader.readAsDataURL(file);
    });
  };

  // =====================================================
  // IMAGE UPLOAD
  // =====================================================

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      showErrorPopup(
        "Please select a JPG, PNG or WEBP image."
      );

      e.target.value = "";

      return;
    }

    try {
      setUpdating(true);
      setError("");

      console.log(
        "ORIGINAL IMAGE SIZE:",
        (file.size / 1024).toFixed(2),
        "KB"
      );

      const compressedFile =
        await compressImageTo250KB(file, 250);

      console.log(
        "COMPRESSED IMAGE SIZE:",
        (compressedFile.size / 1024).toFixed(2),
        "KB"
      );

      if (compressedFile.size > 250 * 1024) {
        throw new Error(
          "Image compression failed. Please select another image."
        );
      }

      if (
        profileImg &&
        profileImg.startsWith("blob:")
      ) {
        URL.revokeObjectURL(profileImg);
      }

      const previewUrl =
        URL.createObjectURL(compressedFile);

      setProfileImg(previewUrl);

      setProfileFile(compressedFile);

      console.log("FINAL UPLOAD IMAGE:", {
        name: compressedFile.name,
        type: compressedFile.type,
        size: compressedFile.size,
        sizeKB: (
          compressedFile.size / 1024
        ).toFixed(2),
      });
    } catch (err) {
      console.error(
        "IMAGE COMPRESSION ERROR:",
        err
      );

      showErrorPopup(
        err?.message ||
          "Unable to compress image. Please try another image."
      );

      e.target.value = "";
    } finally {
      setUpdating(false);
    }
  };

  // =====================================================
  // UPDATE PROFILE
  // =====================================================

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (updating) {
      return;
    }

    const finalUserId =
      userId || localStorage.getItem("user_id");

    const finalDeviceUuid =
      deviceUuid ||
      localStorage.getItem("device_uuid");

    const trimmedName = name.trim();
    const trimmedAddress = address.trim();
    const trimmedPhone = phoneNo.trim();

    if (!trimmedName) {
      showErrorPopup("Please enter your name.");
      return;
    }

    if (!trimmedAddress) {
      showErrorPopup(
        "Please enter your address or use current location."
      );
      return;
    }

    if (!finalUserId) {
      showErrorPopup("User ID not found.");
      return;
    }

    if (!finalDeviceUuid) {
      showErrorPopup("Device UUID not found.");
      return;
    }

    try {
      setUpdating(true);
      setError("");

      const formData = new FormData();

      formData.append(
        "user_id",
        String(finalUserId)
      );

      formData.append(
        "device_uuid",
        String(finalDeviceUuid)
      );

      formData.append("name", trimmedName);
      formData.append("address", trimmedAddress);
      formData.append(
        "mobile_number",
        trimmedPhone
      );

      if (profileFile) {
        formData.append(
          "profile_image",
          profileFile
        );
      }

      console.log("UPDATING PROFILE:", {
        user_id: finalUserId,
        device_uuid: finalDeviceUuid,
        name: trimmedName,
        location_live: trimmedAddress,
        mobile_number: trimmedPhone,
        image_selected: !!profileFile,
      });

      const response = await fetch(
        UPDATE_USER_API,
        {
          method: "POST",
          body: formData,
        }
      );

      const responseText = await response.text();

      console.log(
        "UPDATE PROFILE RAW RESPONSE:",
        responseText
      );

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}: ${responseText}`
        );
      }

      let result;

      try {
        result = JSON.parse(responseText);
      } catch (jsonError) {
        console.error(
          "INVALID UPDATE JSON:",
          responseText
        );

        throw new Error(
          "Invalid JSON returned by update_username.php"
        );
      }

      console.log(
        "UPDATE PROFILE RESULT:",
        result
      );

      if (!result.success) {
        throw new Error(
          result.message || "Profile update failed."
        );
      }

      const updatedUser = result.user || {};

      const updatedName =
        updatedUser.name ||
        result.name ||
        trimmedName;

      const updatedAddress =
        updatedUser.location_live ||
        result.location_live ||
        result.address ||
        trimmedAddress;

      const updatedPhone =
        updatedUser.mobile_number ||
        result.mobile_number ||
        trimmedPhone;

      const updatedImage =
        updatedUser.profile_url ||
        result.profile_url ||
        makeImageUrl(
          updatedUser.profile_user ||
            result.profile_user
        ) ||
        profileImg;

      localStorage.setItem(
        "user_id",
        String(finalUserId)
      );

      localStorage.setItem(
        "device_uuid",
        String(finalDeviceUuid)
      );

      localStorage.setItem(
        "user_name",
        updatedName
      );

      localStorage.setItem(
        "user_address",
        updatedAddress
      );

      if (updatedImage) {
        localStorage.setItem(
          "profile_user",
          updatedImage
        );

        localStorage.setItem(
          "profile_image_url",
          updatedImage
        );
      }

      setName(updatedName);
      setAddress(updatedAddress);
      setPhoneNo(updatedPhone);

      if (updatedImage) {
        setProfileImg(updatedImage);
      }

      setProfileFile(null);

      if (onUpdate) {
        onUpdate({
          id: finalUserId,
          deviceUuid: finalDeviceUuid,
          name: updatedName,
          address: updatedAddress,
          location_live: updatedAddress,
          phoneNo: updatedPhone,
          mobile_number: updatedPhone,
          profileImg: updatedImage,
          profile_user:
            updatedUser.profile_user ||
            result.profile_user ||
            "",
        });
      }

      setShowNetworkError(false);
      setNetworkErrorMessage("");

      if (onBack) {
        onBack();
      } else {
        navigate(
          `/${finalUserId}/${finalDeviceUuid}/userprofile`
        );
      }
    } catch (err) {
      console.error(
        "UPDATE PROFILE ERROR:",
        err
      );

      const message =
        err?.message ||
        "Unable to update profile. Please try again.";

      setError(message);

      showErrorPopup(message);
    } finally {
      setUpdating(false);
    }
  };

  // =====================================================
  // DELETE ACCOUNT
  // =====================================================

  const handleDeleteAccount = async () => {
    if (deleting || clearing) {
      return;
    }

    const finalUserId =
      userId || localStorage.getItem("user_id");

    const finalDeviceUuid =
      deviceUuid ||
      localStorage.getItem("device_uuid");

    if (!finalUserId) {
      showErrorPopup("User ID not found.");
      return;
    }

    if (!finalDeviceUuid) {
      showErrorPopup("Device UUID not found.");
      return;
    }

    try {
      setDeleting(true);

      const formData = new FormData();

      formData.append(
        "user_id",
        String(finalUserId)
      );

      formData.append(
        "device_uuid",
        String(finalDeviceUuid)
      );

      const response = await fetch(
        DELETE_USER_API,
        {
          method: "POST",
          body: formData,
        }
      );

      const responseText = await response.text();

      console.log(
        "DELETE USER RESPONSE:",
        responseText
      );

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}: ${responseText}`
        );
      }

      let result;

      try {
        result = JSON.parse(responseText);
      } catch {
        throw new Error(
          "Delete API returned invalid JSON."
        );
      }

      if (!result.success) {
        throw new Error(
          result.message ||
            "Account deletion failed."
        );
      }

      /*
      =================================================
      STEP 1: LOCAL STORAGE / SESSION STORAGE CLEAR
      =================================================
      */

      try {
        localStorage.clear();
      } catch (e) {
        console.warn(
          "localStorage clear failed:",
          e
        );
      }

      try {
        sessionStorage.clear();
      } catch (e) {
        console.warn(
          "sessionStorage clear failed:",
          e
        );
      }

      /*
      =================================================
      STEP 2: COOKIES CLEAR (JS accessible)
      =================================================
      */

      try {
        const cookies =
          document.cookie.split(";");

        for (
          let i = 0;
          i < cookies.length;
          i++
        ) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");

          const cookieName =
            eqPos > -1
              ? cookie.substr(0, eqPos).trim()
              : cookie.trim();

          const expire =
            "expires=Thu, 01 Jan 1970 00:00:00 GMT";

          document.cookie = `${cookieName}=;${expire};path=/`;
          document.cookie = `${cookieName}=;${expire};path=/;domain=${window.location.hostname}`;
          document.cookie = `${cookieName}=;${expire};path=/;domain=.${window.location.hostname}`;
        }
      } catch (e) {
        console.warn("cookies clear failed:", e);
      }

      /*
      =================================================
      STEP 3: CACHE STORAGE API
      =================================================
      */

      try {
        if ("caches" in window) {
          const cacheNames =
            await caches.keys();

          await Promise.all(
            cacheNames.map((name) =>
              caches.delete(name)
            )
          );
        }
      } catch (e) {
        console.warn(
          "Cache API clear failed:",
          e
        );
      }

      /*
      =================================================
      STEP 4: INDEXED DB
      =================================================
      */

      try {
        if (
          window.indexedDB &&
          indexedDB.databases
        ) {
          const dbs =
            await indexedDB.databases();

          await Promise.all(
            dbs.map((db) => {
              if (!db.name) {
                return Promise.resolve();
              }

              return new Promise((resolve) => {
                const req =
                  indexedDB.deleteDatabase(
                    db.name
                  );

                req.onsuccess = () =>
                  resolve();
                req.onerror = () =>
                  resolve();
                req.onblocked = () =>
                  resolve();
              });
            })
          );
        }
      } catch (e) {
        console.warn(
          "IndexedDB clear failed:",
          e
        );
      }

      /*
      =================================================
      STEP 5: SERVICE WORKERS
      =================================================
      */

      try {
        if (
          "serviceWorker" in navigator
        ) {
          const registrations =
            await navigator.serviceWorker.getRegistrations();

          await Promise.all(
            registrations.map((reg) =>
              reg.unregister()
            )
          );
        }
      } catch (e) {
        console.warn(
          "Service Worker unregister failed:",
          e
        );
      }

      /*
      =================================================
      STEP 6: HttpOnly COOKIES / SESSION (backend)
      =================================================
      */

      try {
        await fetch(CLEAR_SITE_DATA_API, {
          method: "POST",
          credentials: "include",
        });
      } catch (e) {
        // Agar endpoint nahi hai to ignore karo
        console.warn(
          "Backend clear BroBizee data skipped:",
          e
        );
      }

      setShowDeletePopup(false);

      /*
      =================================================
      STEP 7: ACCURATE NAVIGATE TO "/"
      =================================================
      */

      navigate("/", { replace: true });
    } catch (err) {
      console.error(
        "DELETE ACCOUNT ERROR:",
        err
      );

      showErrorPopup(
        err?.message ||
          "Account deletion failed. Please try again."
      );
    } finally {
      setDeleting(false);
    }
  };

  // =====================================================
  // CLEAR SITE DATA (only current website)
  // =====================================================

  const handleClearSiteData = async () => {
    if (clearing || deleting) {
      return;
    }

    setClearing(true);
    setClearStatus("Clearing BroBizee data...");

    const result = {
      localStorage: false,
      sessionStorage: false,
      cookies: false,
      caches: false,
      indexedDB: false,
      serviceWorkers: false,
      backend: false,
    };

    try {
      /*
      ------------------------------------------------
      1. LOCAL STORAGE
      ------------------------------------------------
      */

      try {
        localStorage.clear();
        result.localStorage = true;
      } catch (e) {
        console.warn(
          "localStorage clear failed:",
          e
        );
      }

      /*
      ------------------------------------------------
      2. SESSION STORAGE
      ------------------------------------------------
      */

      try {
        sessionStorage.clear();
        result.sessionStorage = true;
      } catch (e) {
        console.warn(
          "sessionStorage clear failed:",
          e
        );
      }

      /*
      ------------------------------------------------
      3. COOKIES (current domain)
      ------------------------------------------------
      */

      try {
        const cookies =
          document.cookie.split(";");

        for (
          let i = 0;
          i < cookies.length;
          i++
        ) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf("=");

          const cookieName =
            eqPos > -1
              ? cookie.substr(0, eqPos).trim()
              : cookie.trim();

          const expire =
            "expires=Thu, 01 Jan 1970 00:00:00 GMT";

          document.cookie = `${cookieName}=;${expire};path=/`;
          document.cookie = `${cookieName}=;${expire};path=/;domain=${window.location.hostname}`;
          document.cookie = `${cookieName}=;${expire};path=/;domain=.${window.location.hostname}`;
        }

        result.cookies = true;
      } catch (e) {
        console.warn(
          "cookies clear failed:",
          e
        );
      }

      /*
      ------------------------------------------------
      4. CACHE STORAGE API
      ------------------------------------------------
      */

      try {
        if ("caches" in window) {
          const cacheNames =
            await caches.keys();

          await Promise.all(
            cacheNames.map((name) =>
              caches.delete(name)
            )
          );

          result.caches = true;
        }
      } catch (e) {
        console.warn(
          "Cache API clear failed:",
          e
        );
      }

      /*
      ------------------------------------------------
      5. INDEXED DB
      ------------------------------------------------
      */

      try {
        if (
          window.indexedDB &&
          indexedDB.databases
        ) {
          const dbs =
            await indexedDB.databases();

          await Promise.all(
            dbs.map((db) => {
              if (!db.name) {
                return Promise.resolve();
              }

              return new Promise((resolve) => {
                const req =
                  indexedDB.deleteDatabase(
                    db.name
                  );

                req.onsuccess = () =>
                  resolve();
                req.onerror = () =>
                  resolve();
                req.onblocked = () =>
                  resolve();
              });
            })
          );

          result.indexedDB = true;
        }
      } catch (e) {
        console.warn(
          "IndexedDB clear failed:",
          e
        );
      }

      /*
      ------------------------------------------------
      6. SERVICE WORKERS
      ------------------------------------------------
      */

      try {
        if ("serviceWorker" in navigator) {
          const registrations =
            await navigator.serviceWorker.getRegistrations();

          await Promise.all(
            registrations.map((reg) =>
              reg.unregister()
            )
          );

          result.serviceWorkers = true;
        }
      } catch (e) {
        console.warn(
          "Service Worker unregister failed:",
          e
        );
      }

      /*
      ------------------------------------------------
      7. BACKEND (HttpOnly cookies + PHP session)
      ------------------------------------------------
      */

      try {
        await fetch(CLEAR_SITE_DATA_API, {
          method: "POST",
          credentials: "include",
        });

        result.backend = true;
      } catch (e) {
        // Endpoint optional hai
        console.warn(
          "Backend clear BroBizee data skipped:",
          e
        );
      }

      console.log(
        "Clear BroBizee data result:",
        result
      );

      setClearStatus(
        "✅ BroBizee data cleared successfully."
      );

      /*
      ------------------------------------------------
      ACCURATE NAVIGATE TO "/"
      ------------------------------------------------
      */

      setTimeout(() => {
        navigate("/", { replace: true });
      }, 700);
    } catch (error) {
      console.error(
        "Clear BroBizee data error:",
        error
      );

      setClearStatus(
        "❌ Failed to clear some BroBizee data."
      );

      setTimeout(() => {
        setClearStatus("");
      }, 3000);
    } finally {
      setClearing(false);
    }
  };

  // =====================================================
  // CLOSE DELETE POPUP
  // =====================================================

  const closeDeletePopup = () => {
    if (deleting || clearing) {
      return;
    }

    setShowDeletePopup(false);
    setClearStatus("");
  };

  // =====================================================
  // CLOSE ERROR POPUP
  // =====================================================

  const closeErrorPopup = () => {
    setShowNetworkError(false);
    setNetworkErrorMessage("");
  };

  // =====================================================
  // HANDLE GO HOME (NEW)
  // =====================================================

  const handleGoHome = () => {
    try {
      localStorage.removeItem("user_id");
      localStorage.removeItem("device_uuid");
      localStorage.removeItem("user_name");
      localStorage.removeItem("user_address");
      localStorage.removeItem("profile_user");
      localStorage.removeItem("profile_image_url");
      localStorage.removeItem("current_latitude");
      localStorage.removeItem("current_longitude");
      localStorage.removeItem("current_address");
    } catch (e) {
      console.warn("Clear user data failed:", e);
    }

    setShowNetworkError(false);
    setNetworkErrorMessage("");

    navigate("/", { replace: true });
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className={styles.screenWrapper}>
        <div className={styles.deviceFrame}>
          <div className={styles.scrollContainer}>
            <main
              className={styles.contentSection}
            >
              <h1 className={styles.pageTitle}>
                Loading profile...
              </h1>
            </main>
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // ERROR
  // =====================================================

  if (error) {
    return (
      <>
        <div className={styles.screenWrapper}>
          <div className={styles.deviceFrame}>
            <div
              className={styles.scrollContainer}
            >
              <main
                className={styles.contentSection}
              >
                <h1 className={styles.pageTitle}>
                  Profile Error
                </h1>

                <p>{error}</p>

                {/* RETRY BUTTON */}

                <button
                  type="button"
                  className={styles.updateBtn}
                  onClick={() =>
                    window.location.reload()
                  }
                >
                  Retry
                </button>

                {/* NEW BUTTON — only when user not found / data.success === false */}

                {userNotFound && (
                  <button
                    type="button"
                    className={styles.updateBtn}
                    style={{
                      marginTop: "12px",
                      background: "#FF6B00",
                      color: "#005CA9",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                    onClick={handleGoHome}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Go to Home / Register
                  </button>
                )}
              </main>
            </div>
          </div>
        </div>

        {showNetworkError && (
          <div
            className="network-screen-wrapper"
            onClick={closeErrorPopup}
          >
            <div
              className="network-modal-card"
              onClick={(e) =>
                e.stopPropagation()
              }
            >
              <div className="network-header">
                <div className="network-icon">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#005CA9"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                    />

                    <line
                      x1="9"
                      y1="9"
                      x2="9.01"
                      y2="9"
                      strokeWidth="3"
                    />

                    <line
                      x1="15"
                      y1="9"
                      x2="15.01"
                      y2="9"
                      strokeWidth="3"
                    />

                    <line
                      x1="9"
                      y1="15"
                      x2="15"
                      y2="15"
                    />
                  </svg>
                </div>

                <h2 className="network-title">
                  Error
                </h2>
              </div>

              <p className="network-message">
                {networkErrorMessage}
              </p>

              <button
                type="button"
                className="network-ok-btn"
                onClick={closeErrorPopup}
              >
                OK
              </button>
            </div>
          </div>
        )}

        <style>{`
          .network-screen-wrapper {
            position: fixed;
            inset: 0;
            z-index: 99999;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 10px;
            box-sizing: border-box;
            background: rgba(0, 0, 0, 0.28);
            backdrop-filter: blur(7px);
            -webkit-backdrop-filter: blur(7px);
            font-family:
              -apple-system,
              BlinkMacSystemFont,
              "SF Pro Text",
              "Segoe UI",
              Roboto,
              sans-serif;
          }

          .network-modal-card {
            width: 88%;
            max-width: 370px;
            background: rgba(255, 255, 255, 0.98);
            border-radius: 20px;
            padding: 28px 22px;
            text-align: center;
            box-sizing: border-box;
            box-shadow:
              0 12px 35px rgba(0, 0, 0, 0.14);
            animation:
              networkPopup 0.28s
              cubic-bezier(0.16, 1, 0.3, 1);
          }

          .network-header {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 10px;
            margin-bottom: 14px;
          }

          .network-icon {
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .network-title {
            margin: 0;
            font-size: 20px;
            font-weight: 700;
            color: #111;
          }

          .network-message {
            margin: 0 auto 24px;
            max-width: 290px;
            font-size: 14px;
            line-height: 1.5;
            color: #555;
          }

          .network-ok-btn {
            width: 100%;
            height: 48px;
            border: none;
            border-radius: 12px;
            background: #005CA9;
            color: #FF6B00;
            font-size: 16px;
            font-weight: 700;
            cursor: pointer;
            transition: 0.2s ease;
            box-shadow:
              0 5px 14px rgba(0, 92, 169, 0.25);
          }

          .network-ok-btn:hover {
            background: #004C8C;
            transform: translateY(-1px);
          }

          .network-ok-btn:active {
            transform: scale(0.98);
          }

          @keyframes networkPopup {
            from {
              opacity: 0;
              transform: scale(0.92);
            }

            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          @media (max-width: 480px) {
            .network-screen-wrapper {
              padding: 16px;
            }

            .network-modal-card {
              width: 90%;
              padding: 24px 18px;
            }

            .network-title {
              font-size: 18px;
            }

            .network-message {
              font-size: 13px;
            }

            .network-ok-btn {
              height: 45px;
              font-size: 15px;
            }
          }

          @media (min-width: 768px) {
            .network-modal-card {
              max-width: 400px;
            }
          }
        `}</style>
      </>
    );
  }

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      <div className={styles.screenWrapper}>
        <div className={styles.deviceFrame}>
          {/* TOP BAR */}

          <header className={styles.appBar}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={() => navigate(-1)}
              disabled={updating || deleting}
              aria-label="Go Back"
            >
              <svg
                width="20"
                height="16"
                viewBox="0 0 24 16"
                fill="none"
                stroke="#005CA9"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 8H5" />
                <polyline points="12 1 5 8 12 15" />
              </svg>
            </button>
          </header>

          {/* SCROLL */}

          <div className={styles.scrollContainer}>
            <main className={styles.contentSection}>
              <h1 className={styles.pageTitle}>
                Profile Edit
              </h1>

              {/* PROFILE IMAGE */}

              <div className={styles.avatarWrapper}>
                <div
                  className={styles.avatarContainer}
                >
                  {profileImg ? (
                    <img
                      src={profileImg}
                      alt="Profile"
                      className={styles.avatarImg}
                      onError={(e) => {
                        e.currentTarget.style.display =
                          "none";
                      }}
                    />
                  ) : (
                    <div
                      className={styles.avatarImg}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#e8f1f8",
                        color: "#005CA9",
                        fontSize: "42px",
                        fontWeight: "700",
                      }}
                    >
                      {name
                        ? name
                            .charAt(0)
                            .toUpperCase()
                        : "U"}
                    </div>
                  )}

                  <button
                    type="button"
                    className={styles.editAvatarBtn}
                    onClick={() =>
                      fileInputRef.current?.click()
                    }
                    disabled={
                      updating || deleting
                    }
                    aria-label="Edit Profile Picture"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#111111"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageUpload}
                  className={styles.hiddenInput}
                  disabled={updating || deleting}
                />
              </div>

              {/* FORM */}

              <form
                onSubmit={handleUpdate}
                className={styles.formContainer}
              >
                {/* NAME */}

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Name
                  </label>

                  <div className={styles.inputRow}>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) =>
                        setName(e.target.value)
                      }
                      className={styles.textInput}
                      disabled={
                        updating || deleting
                      }
                    />

                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#005CA9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={styles.editIcon}
                    >
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </div>
                </div>

                {/* ADDRESS */}

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Address
                  </label>

                  <div className={styles.inputRow}>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) =>
                        setAddress(e.target.value)
                      }
                      placeholder={
                        locationLoading
                          ? "Getting current location..."
                          : "Enter your address"
                      }
                      className={styles.textInput}
                      disabled={
                        updating ||
                        deleting ||
                        locationLoading
                      }
                    />

                    <button
                      type="button"
                      onClick={loadCurrentAddress}
                      disabled={
                        updating ||
                        deleting ||
                        locationLoading
                      }
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: locationLoading
                          ? "wait"
                          : "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      aria-label="Use Current Location"
                      title="Use Current Location"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#005CA9"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 2v4" />
                        <path d="M12 18v4" />
                        <path d="M2 12h4" />
                        <path d="M18 12h4" />

                        <circle
                          cx="12"
                          cy="12"
                          r="7"
                        />

                        <circle
                          cx="12"
                          cy="12"
                          r="2"
                        />
                      </svg>
                    </button>
                  </div>

                  {locationLoading && (
                    <small
                      style={{
                        display: "block",
                        marginTop: "6px",
                        color: "#005CA9",
                      }}
                    >
                      Getting your current location...
                    </small>
                  )}

                  {locationError && (
                    <small
                      style={{
                        display: "block",
                        marginTop: "6px",
                        color: "#d93025",
                      }}
                    >
                      {locationError}
                    </small>
                  )}

                  {!locationLoading &&
                    !locationError && (
                      <button
                        type="button"
                        onClick={loadCurrentAddress}
                        disabled={
                          updating || deleting
                        }
                        style={{
                          marginTop: "7px",
                          border: "none",
                          background: "transparent",
                          color: "#005CA9",
                          fontSize: "13px",
                          fontWeight: "600",
                          cursor: "pointer",
                          padding: "0",
                        }}
                      >
                        Use Current Location
                      </button>
                    )}
                </div>

                {/* PHONE */}

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Phone No
                  </label>

                  <div className={styles.inputRow}>
                    <input
                      type="text"
                      value={phoneNo}
                      onChange={(e) =>
                        setPhoneNo(e.target.value)
                      }
                      className={styles.textInput}
                      disabled={
                        updating || deleting
                      }
                    />

                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#005CA9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={styles.editIcon}
                    >
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </div>
                </div>

                {/* DARK MODE */}

                <div className={styles.toggleRow}>
                  <span
                    className={styles.toggleLabel}
                  >
                    Dark Mode
                  </span>

                  <button
                    type="button"
                    className={`${styles.togglePill} ${
                      darkMode
                        ? styles.togglePillActive
                        : ""
                    }`}
                    onClick={() =>
                      setDarkMode(!darkMode)
                    }
                    disabled={
                      updating || deleting
                    }
                    aria-label="Toggle Dark Mode"
                  >
                    <div
                      className={styles.toggleThumb}
                    >
                      <span
                        style={{
                          color: "#FF6B00",
                          fontSize: "14px",
                        }}
                      >
                        ☼
                      </span>
                    </div>
                  </button>
                </div>

                {/* DELETE + CLEAR SITE DATA */}

                <div className={styles.deleteRow}>
                  <span
                    className={styles.deleteLabel}
                  >
                    Delete Account.
                  </span>

                  <button
                    type="button"
                    className={styles.deleteIconBtn}
                    aria-label="Delete Account"
                    onClick={() =>
                      setShowDeletePopup(true)
                    }
                    disabled={
                      updating || deleting
                    }
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#005CA9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6" />

                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />

                      <line
                        x1="10"
                        y1="11"
                        x2="10"
                        y2="17"
                      />

                      <line
                        x1="14"
                        y1="11"
                        x2="14"
                        y2="17"
                      />
                    </svg>
                  </button>
                </div>

                {/* UPDATE */}

                <button
                  type="submit"
                  className={styles.updateBtn}
                  disabled={updating || deleting}
                >
                  {updating
                    ? "Updating..."
                    : "Update your profile"}
                </button>
              </form>
            </main>
          </div>

          {/* =================================================
              DELETE POPUP (with Clear Site Data)
          ================================================= */}

          {showDeletePopup && (
            <div
              className={styles.deleteModalOverlay}
              onClick={closeDeletePopup}
            >
              <div
                className={styles.deleteModal}
                onClick={(e) =>
                  e.stopPropagation()
                }
              >
                <h2
                  className={styles.deleteModalTitle}
                >
                  Are You Sure to Delete This
                  <br />
                  Account ?
                </h2>

                {/* CLEAR SITE DATA BUTTON */}

                <button
                  type="button"
                  onClick={handleClearSiteData}
                  disabled={clearing || deleting}
                  style={{
                    width: "100%",
                    marginTop: "14px",
                    padding: "11px 14px",
                    borderRadius: "10px",
                    border:
                      "1.5px dashed #FF6B00",
                    background:
                      "rgba(255, 107, 0, 0.08)",
                    color: "#FF6B00",
                    fontSize: "14px",
                    fontWeight: 700,
                    cursor:
                      clearing || deleting
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      clearing || deleting
                        ? 0.6
                        : 1,
                    transition:
                      "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                  aria-label="Clear BroBizee Data"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 6h18" />

                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />

                    <line
                      x1="10"
                      y1="11"
                      x2="10"
                      y2="17"
                    />

                    <line
                      x1="14"
                      y1="11"
                      x2="14"
                      y2="17"
                    />
                  </svg>

                  {clearing
                    ? "Clearing BroBizee data..."
                    : "Clear BroBizee Data Only"}
                </button>

                {/* CLEAR STATUS */}

                {clearStatus && (
                  <div
                    style={{
                      marginTop: "10px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: clearStatus.startsWith(
                        "✅"
                      )
                        ? "#16a34a"
                        : "#dc2626",
                      textAlign: "center",
                    }}
                  >
                    {clearStatus}
                  </div>
                )}

                {/* ACTIONS */}

                <div
                  className={
                    styles.deleteModalActions
                  }
                >
                  <button
                    type="button"
                    className={styles.deleteBackBtn}
                    onClick={closeDeletePopup}
                    disabled={deleting || clearing}
                  >
                    Back
                  </button>

                  <button
                    type="button"
                    className={styles.deleteConfirmBtn}
                    onClick={handleDeleteAccount}
                    disabled={deleting || clearing}
                  >
                    {deleting
                      ? "Deleting..."
                      : "Confirm"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ERROR POPUP */}

      {showNetworkError && (
        <div
          className="network-screen-wrapper"
          onClick={closeErrorPopup}
        >
          <div
            className="network-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="network-header">
              <div className="network-icon">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#005CA9"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  />

                  <line
                    x1="9"
                    y1="9"
                    x2="9.01"
                    y2="9"
                    strokeWidth="3"
                  />

                  <line
                    x1="15"
                    y1="9"
                    x2="15.01"
                    y2="9"
                    strokeWidth="3"
                  />

                  <line
                    x1="9"
                    y1="15"
                    x2="15"
                    y2="15"
                  />
                </svg>
              </div>

              <h2 className="network-title">
                Error
              </h2>
            </div>

            <p className="network-message">
              {networkErrorMessage}
            </p>

            <button
              type="button"
              className="network-ok-btn"
              onClick={closeErrorPopup}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* ERROR POPUP CSS */}

      <style>{`
        .network-screen-wrapper {
          position: fixed;
          inset: 0;
          z-index: 99999;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 10px;
          box-sizing: border-box;
          background: rgba(0, 0, 0, 0.28);
          backdrop-filter: blur(7px);
          -webkit-backdrop-filter: blur(7px);
          font-family:
            -apple-system,
            BlinkMacSystemFont,
            "SF Pro Text",
            "Segoe UI",
            Roboto,
            sans-serif;
        }

        .network-modal-card {
          width: 88%;
          max-width: 370px;
          background: rgba(255, 255, 255, 0.98);
          border-radius: 20px;
          padding: 28px 22px;
          text-align: center;
          box-sizing: border-box;
          box-shadow:
            0 12px 35px rgba(0, 0, 0, 0.14);
          animation:
            networkPopup 0.28s
            cubic-bezier(0.16, 1, 0.3, 1);
        }

        .network-header {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
        }

        .network-icon {
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .network-title {
          margin: 0;
          font-size: 20px;
          font-weight: 700;
          color: #111;
        }

        .network-message {
          margin: 0 auto 24px;
          max-width: 290px;
          font-size: 14px;
          line-height: 1.5;
          color: #555;
          word-break: break-word;
        }

        .network-ok-btn {
          width: 100%;
          height: 48px;
          border: none;
          border-radius: 12px;
          background: #005CA9;
          color: #FF6B00;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: 0.2s ease;
          box-shadow:
            0 5px 14px rgba(0, 92, 169, 0.25);
        }

        .network-ok-btn:hover {
          background: #004C8C;
          transform: translateY(-1px);
        }

        .network-ok-btn:active {
          transform: scale(0.98);
        }

        @keyframes networkPopup {
          from {
            opacity: 0;
            transform: scale(0.92);
          }

          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @media (max-width: 480px) {
          .network-screen-wrapper {
            padding: 16px;
          }

          .network-modal-card {
            width: 90%;
            padding: 24px 18px;
          }

          .network-title {
            font-size: 18px;
          }

          .network-message {
            font-size: 13px;
          }

          .network-ok-btn {
            height: 45px;
            font-size: 15px;
          }
        }

        @media (min-width: 768px) {
          .network-modal-card {
            max-width: 400px;
          }
        }
      `}</style>
    </>
  );
}